"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/**
 * Web-app activity beacon (2026-10-15, go-to-market engagement). Mounted by
 * AuthenticatedContent for signed-in, non-impersonating viewers; reports to
 * /api/activity:
 *
 * - a `page_view` whenever the route changes (and once on mount);
 * - a `heartbeat` every HEARTBEAT_MS while the tab is visible, and one more
 *   when the tab is hidden or the page is left, so "last seen on this page"
 *   is accurate to within a heartbeat.
 *
 * Only the route path and the clock go over the wire — no identifiers (the
 * session cookie says who), no page contents. Everything is fire-and-forget:
 * a lost beacon costs a data point, never a frame of the page.
 */
export const HEARTBEAT_MS = 30_000;
const ENDPOINT = "/api/activity";

type ActivityKind = "page_view" | "heartbeat";

function send(kind: ActivityKind, path: string, unloading = false): void {
  try {
    const body = JSON.stringify({ events: [{ kind, path, at: Date.now() }] });
    // sendBeacon survives navigation and tab close; keepalive fetch is the
    // fallback where it is unavailable or refuses the payload.
    if (unloading && typeof navigator.sendBeacon === "function") {
      const queued = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      if (queued) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      credentials: "same-origin",
      body,
    }).catch(() => {});
  } catch {
    // Measurement must never break the surface it is observing.
  }
}

export function ActivityTracker({ enabled }: { enabled: boolean }) {
  const pathname = usePathname();
  // The heartbeat timer reads the latest route without re-subscribing on every navigation.
  const currentPath = useRef(pathname);

  // One page_view per route.
  useEffect(() => {
    currentPath.current = pathname;
    if (!enabled || !pathname) return;
    send("page_view", pathname);
  }, [enabled, pathname]);

  // Heartbeats while visible; a closing beat on hide/leave.
  useEffect(() => {
    if (!enabled) return;
    const beat = () => {
      if (document.visibilityState === "visible" && currentPath.current) send("heartbeat", currentPath.current);
    };
    const onVisibility = () => {
      if (!currentPath.current) return;
      if (document.visibilityState === "hidden") send("heartbeat", currentPath.current, true);
      else send("heartbeat", currentPath.current);
    };
    const onPageHide = () => {
      if (currentPath.current) send("heartbeat", currentPath.current, true);
    };
    const timer = window.setInterval(beat, HEARTBEAT_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [enabled]);

  return null;
}
